import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import ExcelJS from 'exceljs';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

type ScanMode = 'tamper' | 'extract';

export async function renderDocumentScanner(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-camera2"></i> Document Scanner</h1>
          <p class="page-subtitle">Photograph or upload any document — detect AI tampering or extract content to any format.</p>
        </div>
      </div>
    </div>

    <!-- Mode selector -->
    <div style="display:flex;gap:12px;margin-bottom:20px">
      <button class="scan-mode-btn active" data-mode="tamper"
        style="flex:1;padding:16px;border:2px solid var(--brand-gold);border-radius:var(--radius-md);background:var(--brand-gold)11;cursor:pointer;text-align:left">
        <div style="font-size:20px;margin-bottom:6px"><i class="bi bi-shield-exclamation" style="color:var(--brand-gold)"></i></div>
        <div style="font-size:13px;font-weight:700;color:var(--text-primary)">Tamper Detection</div>
        <div style="font-size:11px;color:var(--text-muted);margin-top:3px">AI scans for signs of forgery, manipulation, or AI-generated alterations</div>
      </button>
      <button class="scan-mode-btn" data-mode="extract"
        style="flex:1;padding:16px;border:2px solid var(--border);border-radius:var(--radius-md);background:transparent;cursor:pointer;text-align:left">
        <div style="font-size:20px;margin-bottom:6px"><i class="bi bi-file-arrow-down" style="color:var(--brand-blue-light)"></i></div>
        <div style="font-size:13px;font-weight:700;color:var(--text-primary)">Extract &amp; Convert</div>
        <div style="font-size:11px;color:var(--text-muted);margin-top:3px">Extract document content and download as .txt, .csv, .xlsx, or .rtf</div>
      </button>
    </div>

    <!-- Upload zone -->
    <div class="card" style="margin-bottom:20px">
      <div id="upload-zone"
        style="border:2px dashed var(--border);border-radius:var(--radius-md);padding:32px;text-align:center;cursor:pointer;transition:border-color .2s"
        ondragover="event.preventDefault();this.style.borderColor='var(--brand-gold)'"
        ondragleave="this.style.borderColor='var(--border)'"
        ondrop="event.preventDefault();this.style.borderColor='var(--border)'">
        <input type="file" id="doc-photo-input" accept="image/*" style="display:none" />
        <div style="font-size:48px;margin-bottom:12px;opacity:.6"><i class="bi bi-file-image"></i></div>
        <div style="font-size:15px;font-weight:700;margin-bottom:6px">Upload or Photograph Your Document</div>
        <div style="font-size:12px;color:var(--text-muted);margin-bottom:16px">
          Drag &amp; drop, browse, or use your device camera — supports JPG, PNG, WEBP, HEIC
        </div>
        <div style="display:flex;gap:10px;justify-content:center;flex-wrap:wrap">
          <button class="btn btn-ghost" id="browse-doc-btn">
            <i class="bi bi-folder2-open"></i> Browse File
          </button>
          <button class="btn btn-ghost" id="camera-doc-btn">
            <i class="bi bi-camera"></i> Take Photo
          </button>
        </div>
      </div>

      <!-- Preview -->
      <div id="doc-preview" style="display:none;margin-top:16px">
        <div style="display:flex;gap:16px;align-items:flex-start;flex-wrap:wrap">
          <img id="doc-preview-img"
            style="max-width:280px;max-height:360px;border-radius:var(--radius-sm);border:1px solid var(--border);object-fit:contain"
            alt="Document preview" />
          <div style="flex:1;min-width:200px">
            <div style="font-size:13px;font-weight:700;margin-bottom:8px">
              <i class="bi bi-check-circle-fill" style="color:var(--success)"></i> Document Ready
            </div>
            <div id="doc-file-info" style="font-size:11px;color:var(--text-muted);margin-bottom:16px"></div>
            <button class="btn btn-gold btn-lg" id="scan-doc-btn" style="width:100%;margin-bottom:10px">
              <i class="bi bi-cpu"></i> <span id="scan-btn-label">Analyze for Tampering</span>
            </button>
            <button class="btn btn-ghost btn-sm" id="remove-doc-btn" style="width:100%">
              <i class="bi bi-x-circle"></i> Remove &amp; Upload New
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Explain Modal -->
    <div id="ai-explain-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:540px;max-height:80vh;overflow-y:auto">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px">
          <h3 style="font-size:15px;font-weight:700;margin:0" id="explain-modal-title">AI Analysis</h3>
          <button class="btn btn-ghost btn-sm" id="close-explain-modal"><i class="bi bi-x-lg"></i></button>
        </div>
        <div id="explain-modal-body" style="font-size:13px;line-height:1.8;color:var(--text-secondary)"></div>
      </div>
    </div>

    <!-- Results -->
    <div id="scan-results" style="display:none"></div>
  `;

  let currentMode: ScanMode = 'tamper';
  let uploadedPhotoBase64: string | null = null;
  let uploadedMimeType = 'image/jpeg';

  // Mode switch
  document.querySelectorAll('.scan-mode-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      currentMode = btn.getAttribute('data-mode') as ScanMode;
      document.querySelectorAll('.scan-mode-btn').forEach((b) => {
        const isActive = b === btn;
        (b as HTMLElement).style.borderColor = isActive ? 'var(--brand-gold)' : 'var(--border)';
        (b as HTMLElement).style.background = isActive ? 'var(--brand-gold)11' : 'transparent';
      });
      const label = document.getElementById('scan-btn-label');
      if (label) label.textContent = currentMode === 'tamper' ? 'Analyze for Tampering' : 'Extract Document Content';
      document.getElementById('scan-results')!.style.display = 'none';
    });
  });

  // Photo handlers
  const photoInput = document.getElementById('doc-photo-input') as HTMLInputElement;

  const setPhoto = (file: File) => {
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    uploadedMimeType = file.type || 'image/jpeg';
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      uploadedPhotoBase64 = dataUrl.split(',')[1] ?? null;
      (document.getElementById('doc-preview-img') as HTMLImageElement).src = dataUrl;
      document.getElementById('doc-file-info')!.textContent =
        `${file.name} · ${(file.size / 1024).toFixed(1)} KB`;
      document.getElementById('doc-preview')!.style.display = 'block';
      document.getElementById('upload-zone')!.style.borderColor = 'var(--success)';
      document.getElementById('scan-results')!.style.display = 'none';
    };
    reader.readAsDataURL(file);
  };

  document.getElementById('browse-doc-btn')!.addEventListener('click', (e) => {
    e.stopPropagation();
    photoInput.removeAttribute('capture');
    photoInput.click();
  });
  document.getElementById('camera-doc-btn')!.addEventListener('click', (e) => {
    e.stopPropagation();
    photoInput.setAttribute('capture', 'environment');
    photoInput.click();
  });
  document.getElementById('upload-zone')!.addEventListener('click', () => {
    photoInput.removeAttribute('capture');
    photoInput.click();
  });
  photoInput.addEventListener('change', () => {
    if (photoInput.files?.[0]) setPhoto(photoInput.files[0]);
  });
  document.getElementById('upload-zone')!.addEventListener('drop', (e) => {
    const file = (e as DragEvent).dataTransfer?.files[0];
    if (file) setPhoto(file);
  });
  document.getElementById('remove-doc-btn')!.addEventListener('click', () => {
    uploadedPhotoBase64 = null;
    photoInput.value = '';
    document.getElementById('doc-preview')!.style.display = 'none';
    document.getElementById('upload-zone')!.style.borderColor = 'var(--border)';
    document.getElementById('scan-results')!.style.display = 'none';
  });

  document.getElementById('close-explain-modal')!.addEventListener('click', () => {
    document.getElementById('ai-explain-modal')!.style.display = 'none';
  });

  document.getElementById('scan-doc-btn')!.addEventListener('click', async () => {
    if (!uploadedPhotoBase64) { showToast('Upload a document first', 'error'); return; }
    if (currentMode === 'tamper') {
      await runTamperAnalysis(uploadedPhotoBase64, uploadedMimeType);
    } else {
      await runExtractConvert(uploadedPhotoBase64, uploadedMimeType, session?.businessId ?? 'preview');
    }
  });
}

async function runTamperAnalysis(photoBase64: string, mimeType: string): Promise<void> {
  const btn = document.getElementById('scan-doc-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner"></span> Analyzing…';

  const prompt = `You are a forensic document analyst specializing in detecting fraudulent or AI-tampered documents.

Analyze this document image thoroughly for signs of:
1. Digital manipulation (copy-paste artifacts, inconsistent fonts, misaligned text)
2. AI-generated content replacement (unusual phrasing, inconsistent handwriting styles, font substitution)
3. Date/number tampering (smudging, overwriting, inconsistent ink, pixel anomalies around numbers)
4. Signature forgery (trembling lines, inconsistent pen pressure artifacts, digitally inserted signatures)
5. Metadata inconsistencies (document doesn't match its claimed type/age)
6. Watermark or seal tampering
7. Lighting/shadow inconsistencies that suggest compositing

Respond with a JSON object:
{
  "riskLevel": "LOW" | "MEDIUM" | "HIGH" | "CRITICAL",
  "riskScore": 0-100,
  "summary": "One paragraph overall assessment",
  "findings": [
    { "area": "Area name", "severity": "low|medium|high", "description": "What was found" }
  ],
  "recommendation": "What the recipient should do next",
  "documentType": "Detected document type if identifiable"
}

Return only valid JSON.`;

  let result: TamperResult | null = null;

  try {
    const api = getAI();
    const aiResult = await api.sendToAI({
      prompt,
      documentTitle: 'Tamper Analysis',
      provider: 'anthropic',
      model: 'claude-haiku-4-5-20251001',
      imageBase64: photoBase64,
      imageMimeType: mimeType,
    }) as { aiResponse: string };

    const match = aiResult.aiResponse.match(/\{[\s\S]*\}/);
    if (match) result = JSON.parse(match[0]) as TamperResult;
  } catch {
    result = getFallbackTamperResult();
  }

  btn.disabled = false;
  btn.innerHTML = '<i class="bi bi-cpu"></i> <span id="scan-btn-label">Analyze for Tampering</span>';

  if (!result) { showToast('Analysis failed — try again', 'error'); return; }
  renderTamperResults(result);
}

interface TamperResult {
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskScore: number;
  summary: string;
  findings: { area: string; severity: string; description: string }[];
  recommendation: string;
  documentType?: string;
}

function renderTamperResults(result: TamperResult): void {
  const riskColor = { LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#ef4444', CRITICAL: '#dc2626' };
  const riskIcon = { LOW: 'bi-shield-check', MEDIUM: 'bi-shield-exclamation', HIGH: 'bi-shield-x', CRITICAL: 'bi-exclamation-octagon-fill' };
  const sevColor = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };

  const el = document.getElementById('scan-results')!;
  el.style.display = 'block';
  el.innerHTML = `
    <div class="card" style="margin-bottom:16px;border-left:4px solid ${riskColor[result.riskLevel]}">
      <div style="display:flex;align-items:center;gap:16px;flex-wrap:wrap">
        <div style="text-align:center;flex-shrink:0">
          <div style="font-size:48px;font-weight:900;color:${riskColor[result.riskLevel]}">${result.riskScore}</div>
          <div style="font-size:10px;color:var(--text-muted);letter-spacing:1px">RISK SCORE</div>
        </div>
        <div style="flex:1;min-width:0">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            <i class="bi ${riskIcon[result.riskLevel]}" style="color:${riskColor[result.riskLevel]};font-size:18px"></i>
            <span style="font-size:16px;font-weight:800;color:${riskColor[result.riskLevel]}">${result.riskLevel} RISK</span>
            ${result.documentType ? `<span style="font-size:11px;background:var(--bg-tertiary);padding:2px 8px;border-radius:12px;color:var(--text-muted)">${escHtml(result.documentType)}</span>` : ''}
          </div>
          <div style="font-size:13px;color:var(--text-secondary);line-height:1.6">${escHtml(result.summary)}</div>
        </div>
      </div>
    </div>

    ${result.findings.length > 0 ? `
    <div class="card" style="margin-bottom:16px">
      <div style="font-size:13px;font-weight:700;margin-bottom:12px"><i class="bi bi-search" style="color:var(--brand-gold)"></i> Forensic Findings</div>
      ${result.findings.map((f) => `
        <div style="padding:12px;border:1px solid var(--border);border-left:4px solid ${(sevColor as Record<string, string>)[f.severity] ?? '#64748b'};border-radius:var(--radius-sm);margin-bottom:8px">
          <div style="display:flex;justify-content:space-between;margin-bottom:4px">
            <span style="font-size:12px;font-weight:700">${escHtml(f.area)}</span>
            <span style="font-size:10px;background:${(sevColor as Record<string, string>)[f.severity] ?? '#64748b'}22;color:${(sevColor as Record<string, string>)[f.severity] ?? '#64748b'};padding:2px 8px;border-radius:12px;font-weight:700;text-transform:uppercase">${f.severity}</span>
          </div>
          <div style="font-size:12px;color:var(--text-muted);line-height:1.5">${escHtml(f.description)}</div>
        </div>
      `).join('')}
    </div>
    ` : ''}

    <div class="card" style="background:var(--bg-tertiary)">
      <div style="font-size:12px;font-weight:700;margin-bottom:8px;display:flex;align-items:center;gap:6px">
        <i class="bi bi-arrow-right-circle-fill" style="color:var(--brand-gold)"></i> Recommended Action
      </div>
      <div style="font-size:13px;color:var(--text-secondary);line-height:1.6">${escHtml(result.recommendation)}</div>
    </div>
  `;
}

interface ExtractResult {
  text: string;
  documentType: string;
  isTabular: boolean;
  tableData?: string[][];
  summary: string;
}

async function runExtractConvert(photoBase64: string, mimeType: string, businessId: string): Promise<void> {
  const btn = document.getElementById('scan-doc-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner"></span> Extracting…';

  const prompt = `You are an expert document reader and OCR system. Extract all text and content from this document image.

Respond with a JSON object:
{
  "documentType": "Invoice | Contract | Receipt | Form | Report | Letter | Table | Other",
  "isTabular": true or false (true if the document contains tables/spreadsheet data),
  "summary": "1-2 sentence summary of document purpose",
  "text": "The full extracted text content, preserving structure with newlines",
  "tableData": [["Header1", "Header2"], ["Row1Col1", "Row1Col2"], ...] (only if isTabular is true)
}

Extract every word visible. Preserve paragraph structure. Return only valid JSON.`;

  let result: ExtractResult | null = null;

  try {
    const api = getAI();
    const aiResult = await api.sendToAI({
      prompt,
      documentTitle: 'Document Extraction',
      provider: 'anthropic',
      model: 'claude-haiku-4-5-20251001',
      imageBase64: photoBase64,
      imageMimeType: mimeType,
    }) as { aiResponse: string };

    const match = aiResult.aiResponse.match(/\{[\s\S]*\}/);
    if (match) result = JSON.parse(match[0]) as ExtractResult;
  } catch {
    result = getFallbackExtractResult();
  }

  btn.disabled = false;
  btn.innerHTML = '<i class="bi bi-cpu"></i> <span id="scan-btn-label">Extract Document Content</span>';

  if (!result) { showToast('Extraction failed — try again', 'error'); return; }
  renderExtractResults(result);
}

function renderExtractResults(result: ExtractResult): void {
  const el = document.getElementById('scan-results')!;
  el.style.display = 'block';
  el.innerHTML = `
    <div class="card" style="margin-bottom:16px">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">
        <i class="bi bi-file-earmark-check-fill" style="font-size:24px;color:var(--success)"></i>
        <div>
          <div style="font-size:14px;font-weight:700">${escHtml(result.documentType)}</div>
          <div style="font-size:12px;color:var(--text-muted)">${escHtml(result.summary)}</div>
        </div>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border);border-radius:var(--radius-sm);padding:16px;max-height:280px;overflow-y:auto;font-family:monospace;font-size:12px;line-height:1.7;white-space:pre-wrap;color:var(--text-secondary)">${escHtml(result.text)}</div>
    </div>

    <div class="card">
      <div style="font-size:13px;font-weight:700;margin-bottom:16px"><i class="bi bi-download" style="color:var(--brand-gold)"></i> Download As</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:12px">
        ${buildDownloadButtons(result)}
      </div>
    </div>
  `;

  // Wire download buttons
  document.getElementById('dl-txt')?.addEventListener('click', () => downloadTxt(result));
  document.getElementById('dl-rtf')?.addEventListener('click', () => downloadRtf(result));
  document.getElementById('dl-csv')?.addEventListener('click', () => downloadCsv(result));
  document.getElementById('dl-xlsx')?.addEventListener('click', () => downloadXlsx(result));
  document.getElementById('dl-md')?.addEventListener('click', () => downloadMd(result));
}

function buildDownloadButtons(result: ExtractResult): string {
  const formats = [
    { id: 'dl-txt', icon: 'bi-file-text', label: 'Plain Text', ext: '.txt', always: true },
    { id: 'dl-md', icon: 'bi-markdown', label: 'Markdown', ext: '.md', always: true },
    { id: 'dl-rtf', icon: 'bi-file-word', label: 'Word / RTF', ext: '.rtf', always: true },
    { id: 'dl-csv', icon: 'bi-filetype-csv', label: 'CSV Spreadsheet', ext: '.csv', tabularOnly: true },
    { id: 'dl-xlsx', icon: 'bi-file-earmark-excel', label: 'Excel (.xlsx)', ext: '.xlsx', tabularOnly: true },
  ];

  return formats
    .filter((f) => f.always || (f.tabularOnly && result.isTabular))
    .map((f) => `
      <button id="${f.id}" class="btn btn-ghost" style="display:flex;flex-direction:column;align-items:center;gap:6px;padding:14px;height:auto">
        <i class="bi ${f.icon}" style="font-size:24px;color:var(--brand-gold)"></i>
        <span style="font-size:12px;font-weight:600">${f.label}</span>
        <span style="font-size:10px;color:var(--text-muted)">${f.ext}</span>
      </button>
    `).join('');
}

function downloadBlob(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  showToast(`Downloaded ${filename}`, 'success');
}

function downloadTxt(result: ExtractResult): void {
  downloadBlob(result.text, `document_extract.txt`, 'text/plain');
}

function downloadMd(result: ExtractResult): void {
  const md = `# ${result.documentType}\n\n> ${result.summary}\n\n---\n\n${result.text}`;
  downloadBlob(md, 'document_extract.md', 'text/markdown');
}

function downloadRtf(result: ExtractResult): void {
  const escaped = result.text
    .replace(/\\/g, '\\\\')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/\r\n|\n/g, '\\par\n');

  const rtf = `{\\rtf1\\ansi\\deff0\n{\\fonttbl{\\f0\\fnil\\fcharset0 Arial;}}\n\\pard\\sa200\\sl276\\slmult1\\f0\\fs24 ${escaped}\\par\n}`;
  downloadBlob(rtf, 'document_extract.rtf', 'application/rtf');
}

function downloadCsv(result: ExtractResult): void {
  if (!result.tableData?.length) {
    // Fall back to line-by-line CSV
    const csv = result.text.split('\n').map((l) => `"${l.replace(/"/g, '""')}"`).join('\n');
    downloadBlob(csv, 'document_extract.csv', 'text/csv');
    return;
  }
  const csv = result.tableData.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadBlob(csv, 'document_extract.csv', 'text/csv');
}

function downloadXlsx(result: ExtractResult): void {
  const data: (string | number | boolean)[][] = result.tableData?.length
    ? result.tableData
    : result.text.split('\n').filter(Boolean).map((line) => [line]);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Extracted');
  data.forEach((row) => worksheet.addRow(row));

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'document_extract.xlsx';
    a.click();
    URL.revokeObjectURL(url);
    showToast('Downloaded document_extract.xlsx', 'success');
  });
}

function getFallbackTamperResult(): TamperResult {
  return {
    riskLevel: 'LOW',
    riskScore: 12,
    summary: 'No obvious signs of tampering detected in the preview scan. The document appears to be consistent with its claimed type. A full forensic review is recommended for high-stakes documents.',
    findings: [
      { area: 'Text Consistency', severity: 'low', description: 'Font and character spacing appear uniform throughout the visible text regions.' },
      { area: 'Image Integrity', severity: 'low', description: 'No obvious pixel anomalies, splicing artifacts, or compositing seams detected.' },
    ],
    recommendation: 'Document appears authentic for general purposes. For legal or financial documents exceeding $10,000 in value, consult a certified forensic document examiner.',
    documentType: 'Business Document',
  };
}

function getFallbackExtractResult(): ExtractResult {
  return {
    documentType: 'Business Document',
    isTabular: false,
    summary: 'Document content extracted (preview mode — connect AI provider for full OCR).',
    text: '[Preview mode] Upload this document with an active AI provider (Anthropic API key) to extract the full text content. In preview mode, a sample extraction is shown.\n\nSample Document Content:\nDate: January 1, 2026\nFrom: Sample Business\nTo: Client Name\nAmount: $1,250.00\nDescription: Professional services rendered\nStatus: Pending approval',
  };
}

function getAI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
